'use client'

import clsx from 'clsx'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { useEffectOnce } from '@src/hooks/useEffectOnce'
import { cn } from '@src/utils/tailwind'

interface CarouselProps {
  slides: React.ReactNode[]
  slidesPerView: number
  initialSlide?: number
  className?: string
  carouselClassName?: string
  slideClassName?: string
  onSlideChanged?: (currentSlide: number) => void
  handlersRef?: React.RefObject<CarouselHandlers | null>
  dots?: { color: string; activeColor: string }
}

export interface CarouselHandlers {
  moveToIndex: (index: number) => void
  next: () => void
  previous: () => void
}

export const ProductCarousel: React.FC<CarouselProps> = ({
  slides = [],
  slidesPerView = 1,
  initialSlide = 0,
  className,
  carouselClassName,
  slideClassName,
  onSlideChanged,
  handlersRef,
  dots,
}) => {
  const [currentSlide, setCurrentSlide] = useState(initialSlide)
  const containerRef = useRef<HTMLDivElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const [isTouchDevice, setIsTouchDevice] = useState(false)
  const [isDragging, setIsDragging] = useState(false)
  const [wasDragged, setWasDragged] = useState(true)
  const dragStart = useRef(0)
  const scrollStart = useRef(0)
  const [pages, setPages] = useState(1)

  const moveToIndex = useCallback(function moveToIndex(
    index: number,
    options?: { behavior?: 'smooth' | 'instant' | 'auto' }
  ) {
    if (!containerRef.current || !trackRef.current) return
    const slide = trackRef.current.children[index] as HTMLDivElement
    if (!slide) return
    const distance = slide.offsetLeft - trackRef.current.offsetLeft
    containerRef.current.scrollTo({
      left: distance,
      behavior: options?.behavior ?? 'smooth',
    })
  }, [])

  const next = useCallback(() => {
    moveToIndex(currentSlide + 1)
  }, [currentSlide, moveToIndex])

  const previous = useCallback(() => {
    moveToIndex(currentSlide - 1)
  }, [currentSlide, moveToIndex])

  const handleScroll = useCallback((event: React.UIEvent) => {
    if (!event.currentTarget || !trackRef.current?.children) return
    const container = event.currentTarget as HTMLDivElement
    const slideElements = Array.from(trackRef.current.children) as HTMLDivElement[]
    const scrollLeft = container.scrollLeft
    const scrollCenter = scrollLeft + container.clientWidth / 2

    const currentSlide = slideElements.reduce((closestIndex, slide, index) => {
      const closestSlide = slideElements[closestIndex]
      const closestSlideCenter = closestSlide.offsetLeft + closestSlide.clientWidth / 2
      const closestSlideDistance = Math.abs(scrollCenter - closestSlideCenter)
      const slideCenter = slide.offsetLeft + slide.clientWidth / 2
      const slideDistance = Math.abs(scrollCenter - slideCenter)
      return slideDistance < closestSlideDistance ? index : closestIndex
    }, 0)

    if (currentSlide >= slideElements.length - 1) {
      setPages((prev) => prev + 1)
    } else if (currentSlide === 0) {
      setPages(1)
    }

    setCurrentSlide(currentSlide)
  }, [])

  const handleMouseDown = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
    setWasDragged(false)
    dragStart.current = event.pageX
    scrollStart.current = containerRef.current!.scrollLeft
    setIsDragging(true)
  }, [])

  const handleMouseUp = useCallback(() => {
    dragStart.current = 0
    setIsDragging(false)
    setWasDragged(true)
    moveToIndex(currentSlide)
  }, [currentSlide, moveToIndex])

  const handleMouseMove = useCallback(
    (event: React.MouseEvent) => {
      if (isTouchDevice || !isDragging) return
      const container = event.currentTarget
      const x = (event.pageX - dragStart.current) * -2
      container.scrollLeft = scrollStart.current + x
    },
    [isDragging, isTouchDevice]
  )

  useEffectOnce(() => {
    moveToIndex(initialSlide, { behavior: 'instant' })
  }, [initialSlide, moveToIndex])

  useEffect(() => {
    if (onSlideChanged) onSlideChanged(currentSlide % slides.length)
  }, [currentSlide, onSlideChanged, slides.length])

  useEffect(() => {
    setIsTouchDevice('ontouchstart' in window)
  }, [])

  useEffect(() => {
    if (handlersRef) handlersRef.current = { moveToIndex, next, previous }
  }, [moveToIndex, handlersRef, next, previous])

  return (
    <>
      <div
        role="button"
        tabIndex={0}
        ref={containerRef}
        onScroll={handleScroll}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        className={clsx(className, [
          'no-scrollbar w-full cursor-grab overflow-x-scroll active:cursor-grabbing',
          isTouchDevice && 'snap-x snap-mandatory scroll-smooth',
          className,
        ])}
      >
        <div
          ref={trackRef}
          style={{ gridAutoColumns: `calc(100%/${slidesPerView})` }}
          className={clsx('grid select-none grid-flow-col', carouselClassName)}
        >
          {new Array<typeof slides>(pages).fill(slides).map((slides, page) =>
            slides.map((slide, index) => (
              <div
                key={`page-${page}-slide-${index}`}
                id={`page-${page}-slide-${index}`}
                className={clsx(slideClassName, {
                  'snap-start snap-always': isTouchDevice || wasDragged,
                })}
              >
                {slide}
              </div>
            ))
          )}
        </div>
      </div>
      <div
        className={cn('mt-8 flex justify-center gap-2 px-3', {
          hidden: slides.length <= 1,
        })}
      >
        {slides.map((_, index) => {
          const isActive = currentSlide % slides.length === index
          return (
            <button
              key={`dot-${index}`}
              aria-label={`Slide ${index + 1}`}
              onClick={() => moveToIndex(index)}
              className={cn('size-3 rounded-full')}
              style={{
                backgroundColor: isActive ? dots?.activeColor : dots?.color,
              }}
            />
          )
        })}
      </div>
    </>
  )
}
